import { useState, useRef, useCallback, useEffect } from "react";
import Notebook from "../Notes/Notebook";
import LeftPanelContent from "../Test/LeftPane/LeftPanelContent";

const MOBILE_BREAKPOINT = 768;

export default function PreviewScreen({
  question,
  onPrev,
  onNext,
  onShare,
  color_left = "#ffffff",
  color_right = "#ffffff",
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [splitPct, setSplitPct] = useState(50);
  const dragging = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onDragStart = useCallback((e) => {
    if (isMobile) return;
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [isMobile]);

  const onDragMove = useCallback((clientX) => {
    if (!dragging.current || !containerRef.current || isMobile) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setSplitPct(Math.min(80, Math.max(20, pct)));
  }, [isMobile]);

  const onDragEnd = useCallback(() => {
    dragging.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => onDragMove(e.clientX);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", onDragEnd);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", onDragEnd);
    };
  }, [onDragMove, onDragEnd]);

  const panelSize = isMobile
    ? window.innerWidth
    : (window.innerWidth * splitPct) / 100;

  const PreviewBar = (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: "#FFFFFF",
        borderRadius: 40,
        padding: "6px 8px",
        boxShadow: "0 4px 32px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)",
        border: "2px solid #E5E7EB",
        zIndex: 100,
      }}
    >
      <button onClick={onPrev} style={navBtnStyle}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>

      <button
        onClick={onShare}
        style={shareBtnStyle}
        onMouseDown={e => e.currentTarget.style.transform = "scale(0.96)"}
        onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
        Share
      </button>

      <button onClick={onNext} style={navBtnStyle}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <div ref={containerRef} style={{ width: "100vw", height: "100vh", overflow: "auto", paddingBottom: 80 }}>
        <div style={{ background: color_left }}>
          <LeftPanelContent question={question} onClick={(...args) => {}}   panelWidth={panelSize} />
        </div>
        <div style={{ background: color_right }}>
          <Notebook question={question} preview={true} />
        </div>
        {PreviewBar}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ display: "flex", flexDirection: "row", width: "100vw", height: "100vh", overflow: "hidden" }}
    >
      <div style={{ width: `${splitPct}%`, height: "100%", background: color_left, flexShrink: 0, overflow: "auto", position: "relative" }}>
        <LeftPanelContent question={question} onClick={(...args) => {}}   panelWidth={panelSize} />
      </div>

      <div
        onMouseDown={onDragStart}
        style={{
          width: "6px", height: "100%", flexShrink: 0, cursor: "col-resize",
          display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5f5",
        }}
      >
        <div style={{ height: 32, width: 4, borderRadius: 2, background: "#ccc" }} />
      </div>

      <div style={{ flex: 1, height: "100%", background: color_right, overflow: "hidden" }}>
        <Notebook question={question} preview={true} />
      </div>

      {PreviewBar}
    </div>
  );
}

const navBtnStyle = {
  display: "flex", alignItems: "center", justifyContent: "center",
  width: 42, height: 42, borderRadius: "50%",
  border: "1.5px solid #E5E7EB", background: "#FAFAFA",
  cursor: "pointer", color: "#6B7280", transition: "all 0.15s",
};

const shareBtnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "linear-gradient(135deg, #FBBF24, #F59E0B)",
  color: "#fff", border: "none", borderRadius: 30,
  padding: "10px 20px", fontSize: 13, fontWeight: 800,
  fontFamily: "'Nunito', sans-serif", cursor: "pointer",
  letterSpacing: "0.02em",
  boxShadow: "0 3px 12px rgba(245,158,11,0.35)",
  transition: "transform 0.15s",
};